import Render from "components/Common/Render";
import { useEffect, useRef, useState } from "react";

interface IReactPopover {
  children: React.ReactNode;
  content: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  trigger?: string;
  open?: boolean;
  size?: number;
  onClose: () => void;
  contentClass?: string;
}

const ReactPopover: React.FC<IReactPopover> = ({
  children,
  content,
  header,
  footer,
  contentClass,
  trigger = "click",
  size = 8,
  open = false,
  onClose,
}) => {
  const [show, setShow] = useState(open);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleMouseOver = () => {
    if (trigger === "hover") {
      setShow(true);
    }
  };

  const handleMouseLeft = () => {
    if (trigger === "hover") {
      setShow(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShow(false);
        onClose();
      }
    };

    if (show) {
      // Bind the event listener
      document.addEventListener("mousedown", handleClickOutside);
      // Disable body scroll
      document.body.style.overflow = "hidden";
    } else {
      // Enable body scroll when not showing
      document.body.style.overflow = "auto";
    }

    return () => {
      // Unbind the event listener and enable body scroll on clean up
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [show, onClose]);

  useEffect(() => {
    // Set show state based on open prop
    setShow(open);
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={handleMouseOver}
      onMouseLeave={handleMouseLeft}
      className="w-full md:relative flex justify-center px-2"
    >
      {/* backdrop */}
      <Render when={show}>
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity z-30"
          onClick={() => {
            setShow(false);
            onClose();
          }}
        ></div>
      </Render>
      <div className={"cursor-pointer"} onClick={() => setShow(!show)}>
        {children}
      </div>
      <div
        hidden={!show}
        className={`md:min-w-[300px] md:max-h-[70vh] max:h-1/2 fixed md:absolute md:right-[0px] md:top-[10px] z-40 transition-all right-[0px] left-[0px] bottom-[0px] ${contentClass}`}
      >
        <div className="md:rounded-sm rounded-t-3xl bg-white p-3 shadow-[10px_30px_150px_rgba(46,38,92,0.25)] md:mb-[10px] md:mt-[40px]">
          <div className="sticky w-full z-50">{header || null}</div>
          <div className="overflow-auto max-h-[70vh] no-scrollbar bg-fixed z-50">
            {content}
          </div>
          <div>{footer || null}</div>
        </div>
      </div>
    </div>
  );
};

export default ReactPopover;
